/**
 * 日報/サマリ(旧「納品件数の入力」。⑨・⑩・⑫・⑬指示 → 2026-09-10 に改称)。現行「サマリ」シートの手入力を階層で見る。
 *  階層: 日付(1) → 会社(2) → 出勤形態×報酬区分(3) → **アポインター(4)**(既定は非表示。上の「アポインター」で出し入れする)
 *  横軸: 取得件数(対応履歴の自動値。手で直した値は「手」の印) ／ 納品件数 ／ 支店別(出勤／在宅) ／ 人件費 ／ 交通費 ／ 会社単価 ／ 売上見込 ／ 原価率 ／ 入力
 *  **このページの「売上見込」は 納品件数 × 会社単価**(2026-09-10 指示。他ページの売上見込=取得×単価とは分母が違う)。原価率 = (人件費＋交通費) ÷ 売上見込
 *  この表は閲覧だけ。入力は各日付の右の「入力」で開く日別のポップアップ(DeliveryDayModal)で行い、その日だけ保存する(2026-09-10 指示)。
 *  PL・会社別に反映。売上実績(納品)ページはこのページに統合して廃止。
 * 2026-09-18 UIデザインシステムの集計表・KPIカードに置き換え(D-90)。未入力・データなしは印で区別する。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HelpTip from '@core/components/HelpTip';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import TableDensity, { useTableDensity } from '@core/components/ui/TableDensity';
import ToneValue from '@core/components/ui/ToneValue';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayTextClass } from '@core/lib/holidays';
import { COST_STYLE_LABELS, DELIVERY_STYLES, priceOn, type Company, type DailySummary, type DateStr, type Delivery, type DeliveryStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { formatCount, formatCurrency, formatPercent } from '@core/utils/formatters';
import CommuteConfirmPanel from './CommuteConfirmPanel';
import ExpenseView from './ExpenseView';
import { deliveryStyleOf } from './calc';
import { costRateTone } from './colors';
import { fetchMonthTargets, fetchSummariesRange } from './dashboardRepo';
import DeliveryDayModal from './DeliveryDayModal';
import { costStyleOf, deliveryKeyOf, emptyCompanyEdit, emptyCompanyInfo, initialEdit, stylesFor, sumCompany, val, type CompanyEdit, type CompanyInfo } from './deliveryEdit';
import { fetchDeliveries } from './deliveryRepo';


function monthDates(month: string): DateStr[] {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y!, m!, 0).getDate();
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}
function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y!, m! - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 支店別(出勤／在宅)の納品件数。在宅 = 在宅歩合＋在宅時給 */
const branchOf = (by: Record<DeliveryStyle, number>) => ({ shinagawa: by.shinagawa, zaitaku: by.buai + by.jikyu });
export default function DeliveryInputPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? todayStr().slice(0, 7);
  const setMonth = useCallback((m: string) => setParams({ month: m }, { replace: true }), [setParams]);
  const dates = useMemo(() => monthDates(month), [month]);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [summaries, setSummaries] = useState<Map<DateStr, DailySummary>>(new Map());
  const [deliveries, setDeliveries] = useState<Map<DateStr, Delivery>>(new Map());
  /** 原価率の目標(月次設定)。超えた行だけ赤字と印を付ける */
  const [costTarget, setCostTarget] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  /** 表示する値(保存済みの内訳。未保存の項目は自動計算): 日付 → 会社 → 内訳 */
  const [edit, setEdit] = useState<Record<DateStr, Record<string, CompanyEdit>>>({});
  /** 開いている日付 ／ 開いている会社("date|cid")。既定はどちらも閉じる(1段ずつ開く) */
  const [openDays, setOpenDays] = useState<Set<DateStr>>(new Set());
  const [openCompanies, setOpenCompanies] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  /** 階層4(アポインター。取得・人件費・交通費は日次集計からの自動値)。**既定は非表示**で、必要なときだけ開く(2026-09-20 発注者指示。D-97) */
  const [showAppointers, setShowAppointers] = useState(false);
  const [density, setDensity] = useTableDensity('cc.deliveries.density');
  /** 入力ポップアップを開いている日付 */
  const [dayModal, setDayModal] = useState<DateStr | null>(null);
  /** ポップアップに未保存の変更があるか(別画面・別の月へ移るときの確認に使う) */
  const [modalDirty, setModalDirty] = useState(false);
  /** 経費画面の未保存 */
  const [expenseDirty, setExpenseDirty] = useState(false);
  /** 納品件数 ／ 経費 の切替(2026-09-10 指示) */
  const [tab, setTab] = useState<'delivery' | 'expense'>('delivery');
  const guard = useUnsavedGuard(modalDirty || expenseDirty);

  useEffect(() => {
    void fetchCompanies()
      .then(setCompanies)
      .catch(() => toast('会社マスタの読み込みに失敗しました。再読み込みしてください'));
  }, [toast]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const from = dates[0]!;
      const to = dates[dates.length - 1]!;
      const [s, d, t] = await Promise.all([fetchSummariesRange(from, to), fetchDeliveries(from, to), fetchMonthTargets(month)]);
      setSummaries(new Map(s.map((x) => [x.date, x])));
      setDeliveries(new Map(d.map((x) => [x.date, x])));
      setCostTarget(t.cost_rate_total ?? null);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [dates, month, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const active = useMemo(() => companies.filter((c) => c.status === 'active').sort((a, b) => a.id.localeCompare(b.id)), [companies]);

  /** 日付 → 会社 → 自動計算(日次集計)。取得は管理者も含める(出勤)。人件費・交通費は管理者を除く(D-28) */
  const info = useMemo(() => {
    const out: Record<DateStr, Record<string, CompanyInfo>> = {};
    for (const [date, s] of summaries) {
      const map: Record<string, CompanyInfo> = {};
      for (const r of s.staff) {
        if (!r.company_id) continue;
        const ci = (map[r.company_id] ??= emptyCompanyInfo());
        if (r.work_style === 'kanri') {
          ci.appts[deliveryStyleOf(r)] += r.appointments;
          continue;
        }
        const cost = costStyleOf(r);
        let op = ci.ops.find((o) => o.op_id === r.op_id && o.cost === cost);
        if (!op) {
          op = { op_id: r.op_id, name: r.name, cost, appts: 0, labor: 0, commute: 0 };
          ci.ops.push(op);
        }
        op.appts += r.appointments;
        op.labor += r.pay - r.commute;
        op.commute += r.commute;
        ci.appts[deliveryStyleOf(r)] += r.appointments;
        ci.labor[cost] += r.pay - r.commute;
        ci.commute[cost] += r.commute;
      }
      out[date] = map;
    }
    return out;
  }, [summaries]);

  // 表示する値: 保存済みの内訳。取得件数・人件費・交通費は未保存なら自動計算(⑩・⑬指示)
  useEffect(() => {
    const next: Record<DateStr, Record<string, CompanyEdit>> = {};
    for (const date of dates) {
      const del = deliveries.get(date);
      const byCompany: Record<string, CompanyEdit> = {};
      for (const c of active) {
        const det = del?.details?.[c.id];
        byCompany[c.id] = initialEdit(det, det ? undefined : del?.counts?.[c.id], info[date]?.[c.id]);
      }
      next[date] = byCompany;
    }
    setEdit(next);
  }, [dates, active, deliveries, info]);

  /**
   * その日に **実績のある会社**(取得・納品・人件費・交通費のどれかに数字がある)。「全社」で切替(2026-09-20 発注者指示)。
   * 日付行の「◯社」はこの数と同じ(開いたときに出る会社の数)
   */
  const companiesOf = (date: DateStr) => {
    if (showAll) return active;
    return active.filter((c) => {
      const ci = info[date]?.[c.id];
      const e = edit[date]?.[c.id];
      const s = e ? sumCompany(e) : null;
      const autoAppts = ci ? ci.appts.shinagawa + ci.appts.buai + ci.appts.jikyu : 0;
      const autoCost = ci ? Object.values(ci.labor).reduce((x, y) => x + y, 0) + Object.values(ci.commute).reduce((x, y) => x + y, 0) : 0;
      return autoAppts > 0 || autoCost > 0 || (s !== null && (s.appointments > 0 || s.delivered > 0 || s.labor > 0 || s.commute > 0));
    });
  };
  const stylesOf = (date: DateStr, cid: string, e: CompanyEdit) => stylesFor(info[date]?.[cid], e, showAll);

  const sumDay = (date: DateStr) => {
    const t = { appointments: 0, delivered: 0, byStyle: { shinagawa: 0, buai: 0, jikyu: 0 } as Record<DeliveryStyle, number>, sales: 0, labor: 0, commute: 0 };
    for (const c of active) {
      const e = edit[date]?.[c.id];
      if (!e) continue;
      const s = sumCompany(e);
      t.appointments += s.appointments;
      t.delivered += s.delivered;
      for (const k of DELIVERY_STYLES) t.byStyle[k] += s.byStyle[k];
      t.sales += s.delivered * priceOn(c, date);
      t.labor += s.labor;
      t.commute += s.commute;
    }
    return t;
  };
  const monthTotal = useMemo(() => {
    const t = { appointments: 0, delivered: 0, byStyle: { shinagawa: 0, buai: 0, jikyu: 0 } as Record<DeliveryStyle, number>, sales: 0, labor: 0, commute: 0, days: 0 };
    for (const date of dates) {
      const s = sumDay(date);
      if (s.delivered > 0 || s.labor > 0 || s.commute > 0 || s.appointments > 0) t.days++;
      t.appointments += s.appointments;
      t.delivered += s.delivered;
      for (const k of DELIVERY_STYLES) t.byStyle[k] += s.byStyle[k];
      t.sales += s.sales;
      t.labor += s.labor;
      t.commute += s.commute;
    }
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, edit, active]);

  const toggleIn = (set: (f: (prev: Set<string>) => Set<string>) => void, key: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** 閲覧の値。自動計算と違えば「手」の印を付け、カーソルで自動値を見せる */
  const viewVal = (text: string, edited: boolean, autoText: string) => (
    <span title={edited ? `自動計算は ${autoText}` : undefined}>
      {text}
      {edited && <span className="mark-edited">手</span>}
    </span>
  );
  const toggleBtn = (open: boolean, label: string, onClick: () => void) => (
    <button
      type="button"
      className="report-toggle"
      aria-expanded={open}
      aria-label={`${label}の内訳を${open ? '閉じる' : '開く'}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <IconChevronRight size={14} />
    </button>
  );
  const today = todayStr();
  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5, 7))}月`;
  const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
  const COLS = (showAppointers ? 11 : 10) + 1;
  /** 会社単価 ／ 売上見込(このページは 納品件数 × 会社単価。2026-09-10 指示) ／ 原価率(=(人件費＋交通費)÷売上見込) の3セル */
  const moneyCells = (unit: number | null, sales: number, labor: number, commute: number) => {
    const cost = labor + commute;
    const rate = sales > 0 ? (cost / sales) * 100 : null;
    return (
      <>
        <td className="is-num">{unit === null ? '' : formatCurrency(unit)}</td>
        <td className="is-num">{sales ? formatCurrency(sales) : ''}</td>
        <td className="is-num">
          <ToneValue tone={costRateTone(rate, costTarget)}>{formatPercent(rate)}</ToneValue>
        </td>
      </>
    );
  };
  /** 支店別(出勤／在宅)の2セル。日付行・会社行・合計行に出す */
  const branchCells = (by: Record<DeliveryStyle, number>) => {
    const b = branchOf(by);
    return (
      <>
        <td className="is-num">{b.shinagawa || ''}</td>
        <td className="is-num">{b.zaitaku || ''}</td>
      </>
    );
  };

  return (
    <div className="screen full">
      {guard.modal}
      <PageHeader title="締め作業" meta={monthLabel} />
      <FilterBar label="表示する月">
        <SegmentedControl
          label="表示する内容"
          options={[
            { key: 'delivery', label: '納品件数' },
            { key: 'expense', label: '経費' },
          ]}
          value={tab}
          onChange={setTab}
        />
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1))} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1))} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">{monthLabel}</span>
        {tab === 'delivery' && (
          <span className="ui-hint">
            日次集計 {formatCount(summaries.size)}日 ／ 入力済み {formatCount(deliveries.size)}日
          </span>
        )}
      </FilterBar>

      {/* 交通費の確認・確定(月100時間以上。D-128)。要確認の人がいるときだけ出る */}
      <CommuteConfirmPanel month={month} onChanged={() => void reload()} />

      {tab === 'expense' ? (
        <Panel title={`経費(${monthLabel})`} meta="日ごとに項目別の経費を入れる。項目は空欄に名前を入れると増える">
          <ExpenseView dates={dates} monthLabel={monthLabel} onDirtyChange={setExpenseDirty} />
        </Panel>
      ) : loading ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : (
        <>
          {/* 上部のカードは 2026-09-20 発注者指示で削除(下の表に同じ数字が出ているため) */}
          <Panel
            title={`締め作業(${monthLabel})`}
            actions={
              <>
                <TableDensity value={density} onChange={setDensity} />
                <SegmentedControl
                  label="対象"
                  size="sm"
                  options={[
                    { key: 'used', label: '実績のある会社' },
                    { key: 'all', label: '全社' },
                  ]}
                  value={showAll ? 'all' : 'used'}
                  onChange={(v) => setShowAll(v === 'all')}
                />
                <SegmentedControl
                  label="アポインター"
                  size="sm"
                  options={[
                    { key: 'off', label: '非表示' },
                    { key: 'on', label: '表示' },
                  ]}
                  value={showAppointers ? 'on' : 'off'}
                  onChange={(v) => setShowAppointers(v === 'on')}
                />
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setOpenDays(new Set(dates))}>
                  日付をすべて開く
                </button>
                <button
                  type="button"
                  className="ui-btn ui-btn-secondary ui-btn-sm"
                  onClick={() => {
                    setOpenDays(new Set());
                    setOpenCompanies(new Set());
                  }}
                >
                  すべて閉じる
                </button>
              </>
            }
            note="行を押して 日付 → 会社 → 出勤形態×報酬区分 の順に開きます。入力は日付の右の「入力」から(人件費・交通費の初期値はその日の自動計算)。売上見込 = 納品件数 × 会社単価"
          >
            <div className="report-scroll">
              <table className={`report-table${density === 'compact' ? ' is-compact' : ''}`}>
                <caption className="sr-only">締め作業 {monthLabel}</caption>
                <colgroup>
                  <col style={{ width: 240 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 90 }} />
                  {showAppointers && <col style={{ width: 140 }} />}
                  <col style={{ width: 120 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">日付 ／ 会社 ／ 出勤形態×報酬区分</th>
                    <th scope="col" className="is-num">
                      <HelpTip help={{ title: '取得件数', what: 'その日の対応履歴(アポ〇)から自動で入る件数。手で直すと「手」の印が付き、PLのアポ数・会社別の取得件数はその値を使う', better: '—' }}>取得件数</HelpTip>
                    </th>
                    <th scope="col" className="is-num">納品件数</th>
                    <th scope="col" className="is-num">支店別 出勤</th>
                    <th scope="col" className="is-num">支店別 在宅</th>
                    {showAppointers && <th scope="col">アポインター</th>}
                    <th scope="col" className="is-num">
                      <HelpTip
                        help={{
                          title: '人件費(円)',
                          what: 'PLの売上原価に使う金額。初期値は日次集計からの自動計算(出勤・タイミー＝時給×実稼働、在宅歩合＝取得×件数単価、在宅時給＝時給×BB稼働)。管理者は経費「報酬」側のため含めない',
                          better: '—',
                        }}
                      >
                        人件費
                      </HelpTip>
                    </th>
                    <th scope="col" className="is-num">
                      <HelpTip help={{ title: '交通費(円)', what: '出勤者の往復交通費。初期値はオペレーターマスタのその日の交通費', better: '—' }}>交通費</HelpTip>
                    </th>
                    <th scope="col" className="is-num">会社単価</th>
                    <th scope="col" className="is-num">
                      <HelpTip help={{ title: '売上見込', what: 'このページでは 納品件数 × 会社単価(2026-09-10 指示)。他ページの「売上見込(取得件数×単価)」とは分母が違う', better: '高いほど良い' }}>売上見込</HelpTip>
                    </th>
                    <th scope="col" className="is-num">
                      <HelpTip help={{ title: '原価率', what: '(人件費＋交通費) ÷ 売上見込(納品件数×会社単価)。目標を超えた行は赤字と印', better: '低いほど良い' }}>原価率</HelpTip>
                    </th>
                    <th scope="col" aria-label="入力" />
                  </tr>
                </thead>
                <tbody>
                  {dates.flatMap((date) => {
                    const open = openDays.has(date);
                    const day = sumDay(date);
                    const has = deliveries.has(date) || summaries.has(date);
                    const cs = companiesOf(date);
                    const rows = [
                      <tr key={date} id={`day-${date}`} className={`lv-0 is-clickable${date === today ? ' is-today' : ''}`} onClick={() => toggleIn(setOpenDays, date)}>
                        <td>
                          <span className="tree-cell">
                            {toggleBtn(open, fmtDate(date), () => toggleIn(setOpenDays, date))}
                            <span className={`tree-label ${dayTextClass(date)}`}>{fmtDate(date)}</span>
                            {date === today && <StatusBadge kind="info" label="今日" />}
                            {cs.length > 0 && <span className="ui-hint">{formatCount(cs.length)}社</span>}
                            {summaries.has(date) && !deliveries.has(date) && <StatusBadge kind="notEntered" label="未入力" />}
                            {!has && <StatusBadge kind="noData" />}
                          </span>
                        </td>
                        <td className="is-num">{day.appointments || ''}</td>
                        <td className="is-num">{day.delivered || ''}</td>
                        {branchCells(day.byStyle)}
                        {showAppointers && <td />}
                        <td className="is-num">{day.labor ? formatCurrency(day.labor) : ''}</td>
                        <td className="is-num">{day.commute ? formatCurrency(day.commute) : ''}</td>
                        {moneyCells(null, day.sales, day.labor, day.commute)}
                        <td className="is-center">
                          <button
                            type="button"
                            className="ui-btn ui-btn-secondary ui-btn-sm"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setDayModal(date);
                            }}
                          >
                            入力
                          </button>
                        </td>
                      </tr>,
                    ];
                    if (!open) return rows;
                    if (cs.length === 0) {
                      rows.push(
                        <tr key={`${date}_none`} className="lv-1">
                          <td colSpan={COLS} className="cell-na">
                            <span className="tree-cell lv-1">この日は実績のある会社がありません(「全社」に切り替えるか、右の「入力」から入力できます)</span>
                          </td>
                        </tr>,
                      );
                      return rows;
                    }
                    for (const c of cs) {
                      const key = `${date}|${c.id}`;
                      const copen = openCompanies.has(key);
                      const e = edit[date]?.[c.id] ?? emptyCompanyEdit();
                      const ci = info[date]?.[c.id];
                      const cs2 = sumCompany(e);
                      const unit = priceOn(c, date);
                      rows.push(
                        <tr key={key} className="lv-1 is-clickable" onClick={() => toggleIn(setOpenCompanies, key)}>
                          <td>
                            <span className="tree-cell lv-1">
                              {toggleBtn(copen, c.name, () => toggleIn(setOpenCompanies, key))}
                              <span className="tree-label">{c.name}</span>
                            </span>
                          </td>
                          <td className="is-num">{cs2.appointments || ''}</td>
                          <td className="is-num">{cs2.delivered || ''}</td>
                          {branchCells(cs2.byStyle)}
                          {showAppointers && <td />}
                          <td className="is-num">{cs2.labor ? formatCurrency(cs2.labor) : ''}</td>
                          <td className="is-num">{cs2.commute ? formatCurrency(cs2.commute) : ''}</td>
                          {moneyCells(unit, cs2.delivered * unit, cs2.labor, cs2.commute)}
                          <td />
                        </tr>,
                      );
                      if (!copen) continue;
                      for (const k of stylesOf(date, c.id, e)) {
                        const dk = deliveryKeyOf(k);
                        const autoL = ci?.labor[k] ?? 0;
                        const autoC = ci?.commute[k] ?? 0;
                        const diffL = e.labor[k].trim() === '' ? null : val(e.labor[k]) - autoL;
                        const diffC = e.commute[k].trim() === '' ? null : val(e.commute[k]) - autoC;
                        const ops = (ci?.ops ?? []).filter((o) => o.cost === k);
                        const autoA = dk ? (ci?.appts[dk] ?? 0) : 0;
                        const curA = dk ? e.appointments[dk].trim() : '';
                        const editedA = dk ? (curA === '' ? autoA > 0 : val(curA) !== autoA) : false;
                        rows.push(
                          <tr key={`${key}|${k}`} className="lv-2">
                            <td>
                              <span className="tree-cell lv-2">
                                <span className="cell-na" aria-hidden="true">
                                  └
                                </span>
                                <span className="tree-label">{COST_STYLE_LABELS[k]}</span>
                              </span>
                            </td>
                            <td className="is-num">{dk ? viewVal(curA === '' ? (editedA ? '0' : autoA ? String(autoA) : '') : curA, editedA, String(autoA)) : <span className="cell-na">出勤に含む</span>}</td>
                            <td className="is-num">{dk ? e.delivered[dk] || '' : <span className="cell-na">出勤に含む</span>}</td>
                            <td />
                            <td />
                            {showAppointers && <td className="cell-na">(今後自動反映)</td>}
                            <td className="is-num">{viewVal(e.labor[k].trim() === '' ? '' : formatCurrency(val(e.labor[k])), diffL !== null && diffL !== 0 && autoL > 0, formatCurrency(autoL))}</td>
                            <td className="is-num">{viewVal(e.commute[k].trim() === '' ? '' : formatCurrency(val(e.commute[k])), diffC !== null && diffC !== 0 && autoC > 0, formatCurrency(autoC))}</td>
                            {moneyCells(unit, (dk ? val(e.delivered[dk]) : 0) * unit, val(e.labor[k]), val(e.commute[k]))}
                            <td />
                          </tr>,
                        );
                        if (showAppointers) {
                          for (const o of ops) {
                            rows.push(
                              <tr key={`${key}|${k}|${o.op_id}`} className="lv-3">
                                <td>
                                  <span className="tree-cell lv-3">
                                    <span className="cell-na" aria-hidden="true">
                                      └
                                    </span>
                                    <span className="tree-label">{o.name}</span>
                                  </span>
                                </td>
                                <td className="is-num">{o.appts ? formatCount(o.appts) : <span className="cell-na">0</span>}</td>
                                <td className="is-num cell-na">—</td>
                                <td />
                                <td />
                                <td />
                                <td className="is-num">{o.labor ? formatCurrency(o.labor) : <span className="cell-na">—</span>}</td>
                                <td className="is-num">{o.commute ? formatCurrency(o.commute) : <span className="cell-na">—</span>}</td>
                                <td />
                                <td />
                                <td />
                                <td />
                              </tr>,
                            );
                          }
                        }
                      }
                    }
                    return rows;
                  })}
                </tbody>
                <tfoot>
                  <tr className="is-total">
                    <td>合計({monthLabel})</td>
                    <td className="is-num">{formatCount(monthTotal.appointments)}</td>
                    <td className="is-num">{formatCount(monthTotal.delivered)}</td>
                    {branchCells(monthTotal.byStyle)}
                    {showAppointers && <td />}
                    <td className="is-num">{formatCurrency(monthTotal.labor)}</td>
                    <td className="is-num">{formatCurrency(monthTotal.commute)}</td>
                    {moneyCells(null, monthTotal.sales, monthTotal.labor, monthTotal.commute)}
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>
        </>
      )}
      {dayModal && (
        <DeliveryDayModal
          date={dayModal}
          companies={active}
          info={info[dayModal]}
          initial={edit[dayModal] ?? {}}
          before={deliveries.get(dayModal) ?? null}
          onClose={() => setDayModal(null)}
          onSaved={() => {
            void reload();
          }}
          onDirtyChange={setModalDirty}
        />
      )}
    </div>
  );
}
